import { useEffect, useState } from 'react'
import { tc, useCommonT } from '../../i18n'

// Supabase reports a failed redirect by sending the user BACK to the page it
// was told to return them to, with error fields in the URL: a denied Discord
// consent screen, a misconfigured provider, an expired or already-used magic
// link. By then the modal that started the flow is gone, so without this the
// user lands with no explanation and no idea what to do next.
//
// That landing page is no longer always the homepage (authReturnUrl, 2026-08-24),
// which changes nothing here: this reads the URL's params, never its path, and
// it is mounted at the App root rather than inside any route.
//
// Self-arming: it reads the URL on mount, shows what happened, and scrubs the
// params so a reload or a shared URL does not resurrect the notice.

const ERROR_KEYS = ['error', 'error_code', 'error_description'] as const
const MAX_DESCRIPTION = 200

// Whatever Supabase puts in error_description is attacker-influencable (it
// rides in the URL), so it gets stripped to plain prose before it goes on
// screen: word characters, whitespace and a little punctuation, nothing that
// can carry markup, and capped so a padded URL cannot fill the viewport.
function sanitize(raw: string): string {
  return raw
    .replace(/[^\w\s.,'-]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_DESCRIPTION)
    // The copy below supplies its own full stop, so a description that
    // already ends in one does not read as "expired..".
    .replace(/[.\s]+$/, '')
}

type Carrier = { params: URLSearchParams; inHash: boolean }

function findCarrier(): Carrier | null {
  if (typeof window === 'undefined') return null
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  if (ERROR_KEYS.some(k => hashParams.has(k))) return { params: hashParams, inHash: true }
  const queryParams = new URLSearchParams(window.location.search.replace(/^\?/, ''))
  if (ERROR_KEYS.some(k => queryParams.has(k))) return { params: queryParams, inHash: false }
  return null
}

// PURE: this is the state initializer, so it reads the URL and touches
// nothing. The scrub is the effect's job, because rewriting history is the
// external system this component synchronizes with.
function readRedirectError(): string | null {
  const carrier = findCarrier()
  if (!carrier) return null
  const description = sanitize(
    // eslint-disable-next-line i18next/no-literal-string -- URL param name
    carrier.params.get('error_description')
    // eslint-disable-next-line i18next/no-literal-string -- URL param name
    || carrier.params.get('error_code')
    // eslint-disable-next-line i18next/no-literal-string -- URL param name
    || carrier.params.get('error')
    || '',
  )
  return description || tc('auth.redirect.fallback')
}

// Drop the error params from BOTH halves of the URL, not just the half we
// read for display. Scrubbing only the first-found carrier left the other
// one in the URL, and the notice came back on the next reload.
//
// Non-error params in either half survive: a recovery redirect carries its
// own hash fields that SetPasswordModal still needs. Neither half is rewritten
// at all unless it actually carried an error key.
function scrubRedirectError(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
  const queryParams = new URLSearchParams(url.search.replace(/^\?/, ''))
  const hashHadError = ERROR_KEYS.some(k => hashParams.has(k))
  const queryHadError = ERROR_KEYS.some(k => queryParams.has(k))
  if (!hashHadError && !queryHadError) return

  for (const key of ERROR_KEYS) {
    hashParams.delete(key)
    queryParams.delete(key)
  }
  if (hashHadError) {
    const rest = hashParams.toString()
    url.hash = rest ? `#${rest}` : ''
  }
  if (queryHadError) {
    const rest = queryParams.toString()
    url.search = rest ? `?${rest}` : ''
  }
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

export default function AuthRedirectNotice() {
  const [message, setMessage] = useState<string | null>(readRedirectError)
  const t = useCommonT()

  useEffect(() => {
    scrubRedirectError()
  }, [])

  if (!message) return null

  return (
    <div
      data-testid="auth-redirect-notice"
      className="fixed top-0 inset-x-0 z-50 bg-orange-500/10 border border-orange-500/30 text-orange-300 text-sm px-4 py-2 flex items-start justify-between gap-4 backdrop-blur"
    >
      {/* `message` is Supabase's own description (English data), sanitized. */}
      <p>{t('auth.redirect.problem', { message })}</p>
      <button
        type="button"
        aria-label={t('auth.redirect.dismiss')}
        onClick={() => setMessage(null)}
        className="text-xs text-orange-300/80 hover:text-orange-200 shrink-0 transition-colors"
      >
        {t('auth.redirect.dismiss')}
      </button>
    </div>
  )
}
